import { Radio, RadioGroup } from "@headlessui/react";

import { JSX } from "react";
import { Controller, SubmitHandler, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import Spinner from "@/components/helpers/Spinner/Spinner";
import { IconRadio } from "@/components/icons";
import Input from "@/components/inputs/Input";

import { COLORS } from "@/config/colors";
import {
  useAddCreditCardInit,
  useCreateOrder,
  useCreateOrderAndPerform,
  useGetCreditCards,
  useGetSubscription,
  useGetUser,
  usePerformBonusPayment,
} from "@/hooks/api/user.hooks";
import useModalRouter from "@/hooks/useModalRouter";
import { cn } from "@/utils/cn";
import { onError } from "@/utils/onError";
import { onSuccess } from "@/utils/onSuccess";

import type { CreditCardForm } from "../CreditCardModal/CreditCardModalWrap";
import { MODAL_ROUTES } from "../router/ModalRoutes";
import type { BuySubFrom } from "./types";

export interface ISubsBuyMethod {
  id: number | "balance";
  name: string;
  type: string;
  balance?: number;
  icon?: string;
}

const AddCreditCardForm = () => {
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<CreditCardForm>();
  const { mutate, isPending } = useAddCreditCardInit();
  const { openModal } = useModalRouter();
  const { t } = useTranslation();

  const returnToUrl = window.location.search;

  const onSubmit: SubmitHandler<CreditCardForm> = (data): void => {
    mutate(
      {
        card_number: data.card_number.replace(/\s/g, ""),
        expire_date: data.card_expiration.replace(/\s/g, "").replace("/", ""),
      },
      {
        onSuccess: (response) => {
          openModal([MODAL_ROUTES.CREDIT_CARD, MODAL_ROUTES.CREDIT_CARD_OTP], {
            transaction_id: response.data.transaction_id,
            phone: response.data.phone,
            return_to: returnToUrl,
          });
          onSuccess(t("auth.otp-sent"));
        },
        onError,
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="max-w-[21.25rem]">
        <p className="pb-2">{t("cards.number")}</p>
        <Input
          name="card_number"
          register={register}
          control={control}
          mask="#### #### #### ####"
          inputSize={"medium"}
          validation={{ required: true }}
          errors={errors}
          type={"number"}
          placeholder={t("cards.number")}
        />
        <p className="py-2">{t("cards.expire-date")}</p>
        <Input
          name="card_expiration"
          register={register}
          validation={{ required: true }}
          errors={errors}
          control={control}
          inputSize={"medium"}
          mask="##/##"
          type={"number"}
          className="mb-3 max-w-[10.625rem]"
          placeholder={t("cards.month-year")}
        />
        <Button isLoading={isPending} onClick={handleSubmit(onSubmit)} color="black" size="medium">
          {t("Payment.add-card")}
        </Button>
      </div>
    </form>
  );
};

const BuySubscriptionMethodsModal = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useFormContext<BuySubFrom>();

  const method = watch("method");

  const { data: user } = useGetUser();
  const [params] = useSearchParams();
  const { closeModal } = useModalRouter();
  const { mutate: createOrder, isPending: isOrderLoading } = useCreateOrder();
  const { data: creditCards, isPending: isCardsLoading } = useGetCreditCards();
  const { mutate: createOrderAndPerform, isPending: isPerformLoading } = useCreateOrderAndPerform();
  const { mutate: performBonusPayment, isPending: isPerformBonusLoading } = usePerformBonusPayment();

  const isLoading = isPerformLoading || isOrderLoading || isPerformBonusLoading;

  const subscriptionId = params.get("subscription_id");

  const priceId = params.get("price_id");

  const { openModal } = useModalRouter();

  const subscription = useGetSubscription(+(subscriptionId ?? ""));

  if (!subscriptionId || !priceId) return <></>;

  const price = subscription?.prices.find((item) => item.id == +priceId);

  const balanceInsurance = (price?.price ?? 0) > user.balance;

  const methods: ISubsBuyMethod[] = [
    ...creditCards.map((card) => {
      return {
        id: card.id,
        name: card.number,
        type: "credit_card",
        icon: card.payment_system_image,
      };
    }),
    { id: "balance", name: t("Subscription.from-balance"), type: "balance", balance: user.balance / 100 },
  ];

  const hasBonus = method?.type != "balance" ? subscription?.has_card_bonus : undefined;

  const onSubmit: SubmitHandler<BuySubFrom> = (data): void => {
    if (hasBonus && method?.type == "credit_card") {
      performBonusPayment(
        { bank_card_id: method.id as number },
        {
          onSuccess: (res) => {
            openModal(MODAL_ROUTES.ORDER_PERFORM_SUCCESS, { order_id: res.data.order_id.toString() }, true);
          },
          onError,
        },
      );

      return;
    }

    if (balanceInsurance && data.method?.id == "balance") {
      createOrder(
        {
          order_type: "subscription",
          price_id: price?.id,
          bank_card_id: "payment",
          item_id: +subscriptionId,
          use_cashback: false,
        },
        {
          onSuccess: (res) => {
            openModal(
              [MODAL_ROUTES.TOP_UP, MODAL_ROUTES.TOP_UP_METHODS],
              { tr_id: res.data.id.toString(), payment_type: "subscription" },
              true,
            );
          },
        },
      );
      return;
    }

    createOrderAndPerform(
      {
        order_type: "subscription",
        price_id: price?.id,
        bank_card_id: data.method?.id,
        item_id: +subscriptionId,
        use_cashback: false,
      },
      {
        onSuccess: (res) => {
          openModal(MODAL_ROUTES.ORDER_PERFORM_SUCCESS, { order_id: res.id.toString() }, true);
        },
        onError: (res) => {
          openModal(MODAL_ROUTES.ORDER_PERFORM_ERROR, {}, true);
          onError(res);
        },
      },
    );
  };

  const addCreditCard = { id: "creditCard", name: t("Payment.add-card") };

  if (isCardsLoading) return <Spinner />;

  return (
    <>
      <h2 className={"mb-6 text-center text-3xl font-semibold"}>{t("Subscription.subscription-purchase")}</h2>
      <div className="mb-4 flex items-center gap-3">
        {subscription?.icon && <img src={subscription?.icon} className="h-[3rem]" alt={subscription.title} />}
        <p className="text-2xl font-semibold">{subscription?.title}</p>
      </div>
      <p className="mb-4 text-[#919090]">{subscription?.description}</p>
      {price && (
        <>
          <h3 className={"text-2xl font-semibold"} style={{ color: subscription?.color ?? "#FF5708" }}>
            {t("Subscription.per-30-days", {
              value: ((hasBonus ?? price.price) / 100).thousandSeparator(),
            })}
          </h3>
          {hasBonus && (
            <p className="text-[#919090]">
              {t("Subscription.bonus-description", {
                value: t("Subscription.per-30-days", {
                  value: (price.price / 100).thousandSeparator(),
                }),
              })}
            </p>
          )}
        </>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className={"mt-6"}>
        <Controller
          name={"method"}
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <RadioGroup {...field} aria-label={t("Payment.method")} className={"space-y-3.5"}>
              {({ value }) => (
                <>
                  {creditCards.length == 0 && (
                    <>
                      <Radio
                        as={"div"}
                        value={addCreditCard}
                        id={addCreditCard.id}
                        className={cn(
                          "flex cursor-pointer items-center justify-between rounded-full bg-surface-container-high p-4 outline outline-1 -outline-offset-1 outline-transparent  transition-outline",
                          "focus-visible:outline-on-primary",
                          {
                            "outline-orange-1": value?.id === addCreditCard.id,
                            "outline-error-1": errors.method,
                          },
                        )}
                      >
                        <div className={"flex items-center gap-3"}>
                          <div className={"space-y-2"}>
                            <p className={"text-xl/6"}>{addCreditCard.name}</p>
                          </div>
                        </div>
                        <IconRadio
                          className={"size-6"}
                          color={value?.id === addCreditCard.id ? COLORS["orange-1"] : "#919090"}
                          active={value ? value?.id === addCreditCard.id : false}
                        />
                      </Radio>
                      <AddCreditCardForm />
                    </>
                  )}
                  {methods.map((method) => (
                    <Radio
                      key={method.id}
                      value={method}
                      as={"div"}
                      className={cn(
                        "flex cursor-pointer items-center justify-between rounded-2xl bg-surface-container-high p-4 outline outline-1 -outline-offset-1 outline-transparent  transition-outline",
                        "focus-visible:outline-on-primary",
                        {
                          "outline-orange-1": value?.id === method.id,
                          "outline-error-1": errors.method,
                          "rounded-full": method.id == "balance",
                        },
                      )}
                    >
                      <div className={"flex items-center gap-3"}>
                        {method.icon && <img src={method.icon} className="size-[3.125rem] object-contain" />}
                        <div className={"space-y-2"}>
                          <p className={"text-xl/6"}>{method.name}</p>
                        </div>
                      </div>
                      <IconRadio
                        className={"size-6"}
                        color={value?.id === method.id ? COLORS["orange-1"] : "#919090"}
                        active={value ? value?.id === method.id : false}
                      />
                    </Radio>
                  ))}
                </>
              )}
            </RadioGroup>
          )}
        />
      </form>

      <div className={"mt-6 flex items-end justify-between text-orange-1"}>
        <span className={"text-lg/5 font-medium"}>{t("Subscription.overall")}:</span>
        <div className={"flex flex-col items-end"}>
          {price?.no_discount_price && (
            <span className={"text-base/normal text-on-primary line-through"}>
              {t("General.som", { value: (price?.no_discount_price / 100).thousandSeparator() })}
            </span>
          )}
          {price && (
            <span className={"text-2xl/7 font-semibold"}>
              {t("General.som", { value: ((hasBonus ?? price.price) / 100).thousandSeparator() })}
            </span>
          )}
        </div>
      </div>
      <div className={"mt-4 flex justify-end gap-6 max-lg:grid max-lg:grid-cols-2 max-lg:gap-3"}>
        <Button
          color={"white"}
          onClick={closeModal}
          aria-label={t("Actions.cancel")}
          className={"w-fit min-w-[14.375rem] text-nowrap max-lg:min-w-full"}
        >
          {t("Actions.cancel")}
        </Button>
        <Button
          type={"submit"}
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}
          className={"w-fit min-w-[14.375rem] text-nowrap max-lg:min-w-full"}
        >
          {method?.id == "balance" && balanceInsurance ? t("Subscription.top-up-and-purchase") : t("Actions.buy")}
        </Button>
      </div>
    </>
  );
};

export default BuySubscriptionMethodsModal;
